import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Formik } from 'formik';

import Alert from '@mui/material/Alert';
import Form from 'components/Form';
import AccordionController from 'components/AccordionController';
import General from 'components/HotelEditMainGeneral';
import Presentation from 'components/HotelEditMainPresentation';
import EditorJsField from 'components/EditorJsField';

import {
  EditorJsFieldType,
  useEditorJsField,
  textFieldValue,
  useErrorHandler,
} from 'lib';
import { makeEditorJsInput } from 'lib/graphql/makeEditorJsInput';
import { useCurrentUser } from 'components/CurrentUserProvider';

import { AutocompleteOption } from 'components/Autocomplete';
import { HotelEditMain_hotel$key } from '__generated__/HotelEditMain_hotel.graphql';
import { HotelEditMain_data$key } from '__generated__/HotelEditMain_data.graphql';
import { HotelEditMainMutation } from '__generated__/HotelEditMainMutation.graphql';

type FormValues = {
  name: string;
  city: AutocompleteOption | null;
  stars: number | null;
  location: {
    lat: string;
    lng: string;
  };
  descriptionText: EditorJsFieldType;
  aboutLocationText: EditorJsFieldType;
  features: string[];
  reviews: string[];
};

type Props = {
  hotel: HotelEditMain_hotel$key;
  data: HotelEditMain_data$key;
};

const HotelEditMain: FC<Props> = props => {
  const errorHandler = useErrorHandler();
  const { itemsPerPage } = useCurrentUser();

  const hotel = useFragment(
    graphql`
      fragment HotelEditMain_hotel on Hotel {
        id
        name
        stars
        features
        reviews
        description
        aboutLocation
        source
        userEdited

        city {
          id
          name
        }

        location {
          lat
          lng
        }

        descriptionText {
          ...useEditorJsField_text
          ...EditorJsField_text
        }

        aboutLocationText {
          ...useEditorJsField_text
          ...EditorJsField_text
        }

        ...HotelEditMainGeneral_hotel
      }
    `,
    props.hotel
  );

  const data = useFragment(
    graphql`
      fragment HotelEditMain_data on Query {
        ...HotelEditMainGeneral_data
        ...EditorJsField_data
        ...useEditorJsField_data
      }
    `,
    props.data
  );

  const [update, updating] = useMutation<HotelEditMainMutation>(graphql`
    mutation HotelEditMainMutation(
      $id: ID!
      $input: UpdateHotelInput!
      $first: Int!
      $after: String
      $search: String
      $sort: RoomSorting
    ) {
      updateHotel(id: $id, input: $input) {
        ...HotelEditTabs_hotel
        ...HotelSidebar_hotel
      }
    }
  `);

  const initial: FormValues = {
    name: hotel.name,
    city: hotel.city,
    stars: hotel.stars,
    location: {
      lat: textFieldValue(hotel.location?.lat),
      lng: textFieldValue(hotel.location?.lng),
    },
    descriptionText: useEditorJsField({ text: hotel.descriptionText, data }),
    aboutLocationText: useEditorJsField({
      text: hotel.aboutLocationText,
      data,
    }),
    features: (hotel.features as any) ?? [],
    reviews: (hotel.reviews as any) ?? [],
  };

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const {
        city,
        location: { lat, lng },
        descriptionText,
        aboutLocationText,
        ...rest
      } = values;

      update({
        variables: {
          id: hotel.id,
          input: {
            city: city
              ? {
                  id: city.id,
                }
              : null,

            location:
              lat && lng
                ? {
                    lat: Number(lat),
                    lng: Number(lng),
                  }
                : null,

            descriptionText: makeEditorJsInput(descriptionText),
            aboutLocationText: makeEditorJsInput(aboutLocationText),
            ...rest,
          },
          first: itemsPerPage,
        },
        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, hotel.id, itemsPerPage, update]
  );

  const {
    source,
    userEdited,
    description,
    descriptionText,
    aboutLocation,
    aboutLocationText,
  } = hotel;

  return (
    <Formik<FormValues> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        {source === 'ETG' && userEdited && (
          <Alert severity="warning" variant="filled" sx={{ mb: 2 }}>
            Šį ETG viešbutį redagavo administratoriaus ir visi sekantys
            automatiniai ETG atnaujinimai bus dalinai ignoruojami
          </Alert>
        )}

        <AccordionController id="hotelMain">
          <General hotel={hotel} data={data} />

          <EditorJsField
            name="descriptionText"
            label="Aprašymas"
            text={descriptionText}
            data={data}
            fromHtml={description}
          />

          <EditorJsField
            name="aboutLocationText"
            label="Apie vietovę"
            text={aboutLocationText}
            data={data}
            fromHtml={aboutLocation}
          />

          <Presentation />
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default HotelEditMain;
